import type { ArrowType } from '@ardoq/api-types';
import { markerShapes } from 'tabview/canvasRendering/markerShapes';
import { MODERNIZED_BLOCK_DIAGRAM_NODE_BORDER_WIDTH as NODE_BORDER_WIDTH } from 'yfilesExtensions/styles/modernized/consts';

export const MARKER_SIZE = 5;
export const MARKER_STROKE_WIDTH = 1;

type MarkerExtent = { back: number; front: number };

/** How far a marker reaches behind and ahead of its anchor, in pixels along the reference. */
const markerExtent = (arrowType: ArrowType): MarkerExtent | null => {
  const xs = markerShapes(arrowType).flatMap(({ figures }) =>
    figures.flatMap(figure =>
      figure.kind === 'circle'
        ? [figure.center[0] - figure.radius, figure.center[0] + figure.radius]
        : figure.points.map(([x]) => x)
    )
  );
  if (!xs.length) {
    return null;
  }
  return {
    back: Math.min(...xs) * MARKER_SIZE,
    front: Math.max(...xs) * MARKER_SIZE,
  };
};

/**
 * How far behind the port a marker's anchor goes, so the marker's stroke just
 * meets the outside of the node's border.
 */
export const markerAnchorOffset = (arrowType: ArrowType): number =>
  NODE_BORDER_WIDTH / 2 +
  MARKER_STROKE_WIDTH / 2 +
  Math.max(0, markerExtent(arrowType)?.front ?? 0);

/** How much of the reference, from the node's border, the marker covers. */
export const markerLength = (arrowType: ArrowType): number => {
  const extent = markerExtent(arrowType);
  if (!extent) {
    return 0;
  }
  return markerAnchorOffset(arrowType) - extent.back + MARKER_STROKE_WIDTH / 2;
};
